import type { ReactElement } from 'react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EmailIcon from '@mui/icons-material/Email';
import EngineeringIcon from '@mui/icons-material/Engineering';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FeedbackIcon from '@mui/icons-material/FeedbackOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FolderIcon from '@mui/icons-material/Folder';
import GppMaybeIcon from '@mui/icons-material/GppMaybeOutlined';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HistoryIcon from '@mui/icons-material/History';
import HubIcon from '@mui/icons-material/Hub';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MenuIcon from '@mui/icons-material/Menu';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PublicIcon from '@mui/icons-material/Public';
import PublishIcon from '@mui/icons-material/Publish';
import ReportProblemIcon from '@mui/icons-material/ReportProblemOutlined';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import RouteIcon from '@mui/icons-material/Route';
import RuleIcon from '@mui/icons-material/Rule';
import RvHookupIcon from '@mui/icons-material/RvHookup';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import StarIcon from '@mui/icons-material/Star';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import TuneIcon from '@mui/icons-material/Tune';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import WorkIcon from '@mui/icons-material/Work';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';

/**
 * Ikony centrum ról (decyzja użytkownika 22.10.2026: „zbuduj to ładnie
 * z ikonkami”) — klucz z katalogów API (`role-center/role-catalog.ts`,
 * `permission-catalog.ts`, `ui-elements.ts`) → ikona MUI, spójna z menu
 * (`menuIcons.tsx`). Nieznany klucz — znak zapytania, nie pustka. Test
 * `roleCenterIcons.test.ts` czyta katalogi API i pilnuje, że każdy klucz
 * ma tu ikonę.
 */
const ICONS = {
  AdminPanelSettings: AdminPanelSettingsIcon,
  AltRoute: AltRouteIcon,
  Assignment: AssignmentIcon,
  AssignmentTurnedIn: AssignmentTurnedInIcon,
  Badge: BadgeIcon,
  Business: BusinessIcon,
  CloudUpload: CloudUploadIcon,
  Dashboard: DashboardIcon,
  Description: DescriptionIcon,
  DirectionsCar: DirectionsCarIcon,
  Edit: EditIcon,
  EditNote: EditNoteIcon,
  Email: EmailIcon,
  Engineering: EngineeringIcon,
  EventBusy: EventBusyIcon,
  FactCheck: FactCheckIcon,
  Feedback: FeedbackIcon,
  FilterAlt: FilterAltIcon,
  Folder: FolderIcon,
  GppMaybe: GppMaybeIcon,
  Group: GroupIcon,
  Groups: GroupsIcon,
  Handshake: HandshakeIcon,
  History: HistoryIcon,
  Hub: HubIcon,
  Inventory: InventoryIcon,
  LocalGasStation: LocalGasStationIcon,
  LocalShipping: LocalShippingIcon,
  ManageAccounts: ManageAccountsIcon,
  Menu: MenuIcon,
  MoveToInbox: MoveToInboxIcon,
  Notifications: NotificationsIcon,
  Person: PersonIcon,
  PlaylistAddCheck: PlaylistAddCheckIcon,
  Public: PublicIcon,
  Publish: PublishIcon,
  ReportProblem: ReportProblemIcon,
  RequestQuote: RequestQuoteIcon,
  Route: RouteIcon,
  Rule: RuleIcon,
  RvHookup: RvHookupIcon,
  Security: SecurityIcon,
  SettingsEthernet: SettingsEthernetIcon,
  SpaceDashboard: SpaceDashboardIcon,
  Star: StarIcon,
  Storefront: StorefrontIcon,
  SupervisorAccount: SupervisorAccountIcon,
  SupportAgent: SupportAgentIcon,
  ToggleOn: ToggleOnIcon,
  Tune: TuneIcon,
  VerifiedUser: VerifiedUserIcon,
  ViewTimeline: ViewTimelineIcon,
  Visibility: VisibilityIcon,
  VpnKey: VpnKeyIcon,
  Work: WorkIcon,
} as const;

export const ROLE_CENTER_ICON_KEYS = Object.keys(ICONS);

/** Ikona po kluczu z katalogu; nieznany klucz — znak zapytania. */
export function roleCenterIcon(
  key: string | null | undefined,
  props: { fontSize?: 'inherit' | 'small' | 'medium' | 'large'; color?: 'inherit' | 'primary' | 'action' | 'disabled' } = {},
): ReactElement {
  const Icon = (key && (ICONS as Record<string, typeof HelpOutlineIcon>)[key]) || HelpOutlineIcon;
  return <Icon fontSize={props.fontSize ?? 'small'} color={props.color} />;
}
